import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  CircularProgress,
  Collapse,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Stack,
  InputAdornment,
  Fade,
  Backdrop,
  Skeleton
} from '@mui/material';
import {
  Forum as ForumIcon,
  EditNote as EditIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const AdministrarMensajesDialog = ({
  open,
  onClose,
  contextOptions = [],
  tiposMensaje = [],
  mensajes = [],
  loadingMensajes = false,
  selectedMensaje,
  showMensajeForm,
  newMensaje,
  onNewMensajeChange,
  onSaveMensaje,
  onCancelMensajeForm,
  onEditMensaje,
  onToggleActivoMensaje,
  onDeleteMensaje,
  onCreateNew
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filterContext, setFilterContext] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive

  // Memoized filtered mensajes para mejor performance
  const filteredMensajes = useMemo(() => {
    let filtered = mensajes;

    // Filtrar por contexto
    if (filterContext) {
      filtered = filtered.filter(m => m.contexto?.id === filterContext.id);
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(m => 
        m.mensaje.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.contexto?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.tipo_mensaje?.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(m => 
        filterStatus === 'active' ? m.activo : !m.activo
      );
    }

    return filtered;
  }, [mensajes, filterContext, searchTerm, filterStatus]);

  // Callbacks optimizados
  const handleClose = useCallback(() => {
    onClose();
    if (onCancelMensajeForm) {
      onCancelMensajeForm();
    }
  }, [onClose, onCancelMensajeForm]);

  const toggleExpand = useCallback((id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilterContext(null);
    setSearchTerm('');
    setFilterStatus('all');
  }, []);

  // Memoized color function
  const getContextoColor = useMemo(() => {
    const colors = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];
    return (contextoNombre) => {
      const index = contextoNombre ? contextoNombre.length % colors.length : 0;
      return colors[index];
    };
  }, []);

  // Component para renderizar mensaje individual
  const MensajeCard = React.memo(({ mensaje }) => {
    const isExpanded = expandedItems.has(mensaje.id);
    const shouldShowExpandButton = mensaje.mensaje.length > 120;

    return (
      <Card sx={{ 
        borderRadius: 2,
        border: "1px solid #e0e7ef",
        transition: "all 0.2s ease",
        "&:hover": { 
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
          borderColor: "#6366f1"
        },
        opacity: mensaje.activo ? 1 : 0.6,
        background: mensaje.activo ? "#fff" : "#f8fafc"
      }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Tags mejorados */}
              <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip 
                  label={mensaje.contexto?.nombre || "Sin contexto"}
                  size="small"
                  sx={{
                    background: getContextoColor(mensaje.contexto?.nombre),
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22
                  }}
                />
                <Chip 
                  label={mensaje.tipo_mensaje?.nombre || "Sin tipo"}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
                <Chip 
                  icon={mensaje.activo ? <ShowIcon sx={{ fontSize: 14 }} /> : <HideIcon sx={{ fontSize: 14 }} />}
                  label={mensaje.activo ? "Activo" : "Inactivo"}
                  size="small"
                  sx={{
                    background: mensaje.activo ? "#dcfce7" : "#fef2f2",
                    color: mensaje.activo ? "#16a34a" : "#b91c1c",
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22
                  }}
                />
              </Stack>
              
              {/* Contenido del mensaje */}
              <Box sx={{ position: 'relative' }}>
                <Collapse in={!isExpanded} timeout={200}>
                  <Typography variant="body2" sx={{ 
                    color: "#374151",
                    lineHeight: 1.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  }}>
                    {mensaje.mensaje}
                  </Typography>
                </Collapse>
                
                <Collapse in={isExpanded} timeout={200}>
                  <Typography variant="body2" sx={{ 
                    color: "#374151",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap"
                  }}>
                    {mensaje.mensaje}
                  </Typography>
                </Collapse>
              </Box>
            </Box>
            
            {/* Botones de acción */}
            <Stack direction="row" spacing={0.5} sx={{ ml: 2, flexShrink: 0 }}>
              {shouldShowExpandButton && (
                <Tooltip title={isExpanded ? "Contraer" : "Ver completo"}>
                  <IconButton 
                    size="small"
                    onClick={() => toggleExpand(mensaje.id)}
                    sx={{ color: "#6b7280" }}
                  >
                    {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Editar mensaje">
                <IconButton 
                  size="small" 
                  onClick={() => onEditMensaje(mensaje)}
                  sx={{ 
                    color: "#6366f1",
                    "&:hover": { background: "#6366f108" }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={mensaje.activo ? "Desactivar" : "Activar"}>
                <IconButton 
                  size="small"
                  onClick={() => onToggleActivoMensaje(mensaje)}
                  sx={{ 
                    color: mensaje.activo ? "#f59e0b" : "#10b981",
                    "&:hover": { 
                      background: mensaje.activo ? "#f59e0b08" : "#10b98108" 
                    }
                  }}
                >
                  {mensaje.activo ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Eliminar mensaje">
                <IconButton 
                  size="small"
                  onClick={() => onDeleteMensaje(mensaje)}
                  sx={{ 
                    color: "#ef4444",
                    "&:hover": { background: "#ef444408" }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      TransitionComponent={Fade}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: { backgroundColor: 'rgba(0,0,0,0.7)' }
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          boxShadow: "0 25px 80px rgba(99,102,241,0.2)",
          border: "1px solid rgba(99,102,241,0.1)",
          overflow: "hidden",
          maxHeight: '95vh',
          width: '95vw',
          maxWidth: 1200
        }
      }}
    >
      {/* Header ultra compacto */}
      <DialogTitle sx={{ 
        background: "rgba(30,41,59,0.98)",
        color: "#fff",
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        py: 1.25,
        px: 2.5,
        minHeight: 55
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ForumIcon sx={{ fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
            Gestión de Mensajes
          </Typography>
          <Chip 
            label={`${filteredMensajes.length}/${mensajes.length}`}
            size="small"
            sx={{ 
              background: "rgba(255,255,255,0.2)", 
              color: "#fff", 
              fontWeight: 600 
            }}
          />
        </Stack>
        
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ 
            color: "#fff", 
            background: "rgba(255,255,255,0.15)",
            "&:hover": { background: "rgba(255,255,255,0.25)" }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 'calc(95vh - 55px)' }}>
        {/* Barra de filtros mejorada */}
        <Box sx={{ 
          p: 2, 
          background: "#fff", 
          borderBottom: "1px solid #e0e7ef",
          flexShrink: 0
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar mensajes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={contextOptions}
                getOptionLabel={(option) => option.nombre}
                value={filterContext}
                onChange={(e, newValue) => setFilterContext(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contexto"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <FilterIcon sx={{ color: "#6b7280", fontSize: 16, mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
                clearText="Limpiar"
                noOptionsText="Sin contextos"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Autocomplete
                size="small"
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'active', label: 'Activos' },
                  { value: 'inactive', label: 'Inactivos' }
                ]}
                getOptionLabel={(option) => option.label}
                value={{ value: filterStatus, label: filterStatus === 'all' ? 'Todos' : filterStatus === 'active' ? 'Activos' : 'Inactivos' }}
                onChange={(e, newValue) => setFilterStatus(newValue?.value || 'all')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Estado"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  sx={{ textTransform: "none",
                     borderColor: "rgba(30,41,59,0.3)", // ← CAMBIAR AQUÍ
                    color: "rgba(30,41,59,0.98)", // ← CAMBIAR AQUÍ
                    "&:hover": { 
                      borderColor: "rgba(30,41,59,0.98)", // ← CAMBIAR AQUÍ
                      backgroundColor: "rgba(30,41,59,0.05)", // ← CAMBIAR AQUÍ
                      color: "rgba(30,41,59,0.98)"
                    }
                  }}
                >
                  Limpiar filtros
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={onCreateNew}
                  sx={{
                    background: "rgba(30,41,59,0.98)", // ← CAMBIAR AQUÍ
                    fontWeight: 600,
                    textTransform: "none"
                  }}
                >
                  Nuevo
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        
        {/* Formulario colapsable */}
        <Collapse in={selectedMensaje !== null || showMensajeForm}>
          <Box sx={{ p: 2, background: "#f8fafc", borderBottom: "1px solid #e0e7ef", flexShrink: 0 }}>
            <Card sx={{ 
              borderRadius: 2, 
              border: "2px solid #6366f1",
              boxShadow: "0 8px 32px rgba(99,102,241,0.12)"
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ color: "#6366f1", fontWeight: 700, fontSize: 16 }}>
                    {selectedMensaje ? "✏️ Editar mensaje" : "➕ Nuevo mensaje"}
                  </Typography>
                  <IconButton onClick={onCancelMensajeForm} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      size="small"
                      options={contextOptions}
                      getOptionLabel={(option) => option.nombre}
                      value={contextOptions.find(c => c.id === newMensaje?.id_contexto) || null}
                      onChange={(e, newValue) => onNewMensajeChange({
                        ...newMensaje,
                        id_contexto: newValue ? newValue.id : ""
                      })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contexto"
                          fullWidth
                          required
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      size="small"
                      options={tiposMensaje}
                      getOptionLabel={(option) => option.nombre}
                      value={tiposMensaje.find(t => t.id === newMensaje?.id_tipo_mensaje) || null}
                      onChange={(e, newValue) => onNewMensajeChange({
                        ...newMensaje,
                        id_tipo_mensaje: newValue ? newValue.id : ""
                      })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tipo de mensaje"
                          fullWidth
                          required
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newMensaje?.activo || false}
                          onChange={(e) => onNewMensajeChange({...newMensaje, activo: e.target.checked})}
                          color="primary"
                        />
                      }
                      label={newMensaje?.activo ? "✅ Activo" : "❌ Inactivo"}
                      sx={{ height: '40px', ml: 0 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Mensaje"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      value={newMensaje?.mensaje || ''}
                      onChange={(e) => onNewMensajeChange({...newMensaje, mensaje: e.target.value})}
                      required
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button 
                        variant="outlined"
                        onClick={onCancelMensajeForm}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={onSaveMensaje}
                        disabled={!newMensaje?.id_contexto || !newMensaje?.id_tipo_mensaje || !newMensaje?.mensaje?.trim()}
                        size="small"
                        sx={{ 
                          textTransform: "none",
                          background: "linear-gradient(135deg, #10b981 0%, #16a34a 100%)",
                          fontWeight: 600
                        }}
                      >
                        {selectedMensaje ? 'Actualizar' : 'Crear'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Collapse>
        
        {/* Lista de mensajes con scroll virtual */}
        <Box sx={{ 
          flex: 1,
          p: 2, 
          overflowY: "auto",
          background: "#f8fafc"
        }}>
          {loadingMensajes ? (
            <Grid container spacing={2}>
              {[...Array(6)].map((_, index) => (
                <Grid item xs={12} key={index}>
                  <Skeleton 
                    variant="rectangular" 
                    height={120} 
                    sx={{ borderRadius: 2 }} 
                  />
                </Grid>
              ))}
            </Grid>
          ) : filteredMensajes.length === 0 ? (
            <Box sx={{ 
              textAlign: "center", 
              py: 8,
              color: "#6b7280"
            }}>
              <ForumIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                {searchTerm || filterContext || filterStatus !== 'all' 
                  ? "No se encontraron mensajes" 
                  : "No hay mensajes registrados"
                }
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {searchTerm || filterContext || filterStatus !== 'all' 
                  ? "Intenta modificar los filtros de búsqueda"
                  : "Comienza creando tu primer mensaje predefinido"
                }
              </Typography>
              {!searchTerm && !filterContext && filterStatus === 'all' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onCreateNew}
                  sx={{
                    background: "rgba(30,41,59,0.98)", // ← CAMBIAR AQUÍ TAMBIÉN
                    fontWeight: 600,
                    textTransform: "none",
                    px: 4
                  }}
                >
                  Crear primer mensaje
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredMensajes.map((mensaje) => (
                <Grid item xs={12} lg={6} xl={4} key={mensaje.id}>
                  <MensajeCard mensaje={mensaje} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AdministrarMensajesDialog;